// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { addToEndOfChain, ApiHandler } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { chain as ReorderOrderDetailsChain } from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { chain as ReorderOrderHistoryChain } from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { chain as ReorderOrderStatusChain } from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { parseProductCategories } from "@sc/XDL/Utilities";

type CartLineData = Pick<
    CartLineModel,
    "shortDescription" | "erpNumber" | "pricing" | "qtyOrdered" | "properties" | "brand"
>;

type HandlerType = ApiHandler<
    {
        cartLineCollection?: {
            cartLines?: CartLineModel[];
        };
    },
    {
        cartLines?: CartLineModel[];
    } & CartLineData
>;

export const XngGaReorder: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    if (state.xGoogleAnalytics.reorderEvent) {
        const dataLayer = getDataLayer();
        const session = getSession(state);

        if (!dataLayer) {
            return;
        }

        let cartLinesToAdd: CartLineData[] = [];
        if (props.apiResult?.cartLines || props.apiParameter?.cartLineCollection?.cartLines) {
            // XNG-Changes: this if condition is for Order History Reorder functionality
            if ((props.apiResult?.cartLines as any)?.cartLines) {
                cartLinesToAdd = (props.apiResult?.cartLines as any)?.cartLines || [];
            } else {
                cartLinesToAdd = props.apiResult?.cartLines || props.apiParameter.cartLineCollection?.cartLines || [];
            }
        } else {
            cartLinesToAdd = [
                {
                    shortDescription: props.apiResult.shortDescription || "",
                    erpNumber: props.apiResult.erpNumber || "",
                    pricing: props.apiResult.pricing || null,
                    qtyOrdered: props.apiResult.qtyOrdered || null,
                    properties: props.apiResult.properties,
                    brand: props.apiResult.brand,
                },
            ];
        }

        const totalPrice =
            cartLinesToAdd.length > 1
                ? cartLinesToAdd.reduce((total, line) => {
                      if (line.pricing && line.pricing.extendedUnitNetPrice) {
                          return total + line.pricing.extendedUnitNetPrice;
                      }
                      return total;
                  }, 0)
                : cartLinesToAdd?.[0]?.pricing?.unitNetPrice;
        const googleAnalyticsData = {
            event: state.xGoogleAnalytics.reorderEventName,
            ecommerce: {
                currency: state.context.session.currency?.currencyCode,
                value: totalPrice,
                affiliation: state.xGoogleAnalytics.affiliation,
                sales_rep: state.xGoogleAnalytics.salesRep || "",
                is_rep: state.xGoogleAnalytics.isRep || "",
                bill_to_num: state.xGoogleAnalytics.billToNum || "",
                ship_to_num: state.xGoogleAnalytics.shipToNum || "",
                cust_type: state.xGoogleAnalytics.customerType || "",
                login_type: state.xGoogleAnalytics.loginType || "",
                warehouse: state.xGoogleAnalytics.warehouse || "",
                ...(props.apiResult?.pricing && { value: props.apiResult.pricing.extendedUnitNetPrice }),
                items: cartLinesToAdd.map((cartLine, idx) => ({
                    item_id: cartLine.erpNumber,
                    item_name: cartLine.shortDescription,
                    affiliation: state.xGoogleAnalytics.affiliation,
                    discount: cartLine.pricing
                        ? cartLine.pricing.unitNetPrice < cartLine.pricing.unitListPrice
                            ? cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice
                            : 0
                        : 0,
                    index: idx,
                    item_brand: state.xGoogleAnalytics.includeProductBrand
                        ? (cartLine as CartLineModel).brand?.name || ""
                        : undefined,
                    price: cartLine.pricing?.unitNetPrice,
                    quantity: cartLine.qtyOrdered,
                    item_list_id: state.xGoogleAnalytics.includeProductCategory
                        ? cartLine.properties?.["categoriesIds"] || ""
                        : undefined,
                    location_id: session.pickUpWarehouse?.id,
                    ...parseProductCategories(
                        cartLine?.properties?.itemCategoriesShortDescription,
                        true,
                        state.xGoogleAnalytics.includeProductCategory,
                    ),
                })),
            },
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(googleAnalyticsData);
    }
};

const _GaReorder = XngGaReorder as any;
addToEndOfChain(ReorderOrderDetailsChain, _GaReorder);
addToEndOfChain(ReorderOrderHistoryChain, _GaReorder);
addToEndOfChain(ReorderOrderStatusChain, _GaReorder);
